// One project, every agent that works it - the transpose of the Portfolio page.
// Mirrors lambda_handlers/api/stateful_agents/portfolio/project_status.py.
// See docs/Y2026-Q3/design-project-agent-status-page.md.
import { useQuery } from "react-query";

import type { PortfolioRow } from "./useAgentPortfolio";
import type { GradeGap } from "./useAmazonOverview";
import {
  APPLYING_POLL_MS,
  type ApplyingReview,
  type AttributeDef,
  type DashboardRow,
} from "./useDashboard";
import { useAgentsHttp } from "../useAgentsHttp";
import { QK } from "../queryKeys";

// Mirrors next_actions.py and action_status.py.
export interface ActionStatus {
  status: string;
  /** "internal", "external", or "" when nobody holds it. */
  ball: string;
  reply_due: string;
  reply_basis: string;
  late: boolean;
}

export interface ContactEntry {
  kind: "sent" | "replied";
  at: string;
  evidence: string;
  by: string;
}

/** What a wait needs of one item (next_actions.WaitNeeds). */
export interface WaitNeeds {
  kind: "done" | "closed" | "options" | "outcomes";
  options: number[];
  /** Each needed option's line, as it read when the dependency was written. */
  labels: string[];
  outcomes: string[];
}

/** One change to a date in force (next_actions.DateMove). */
export interface DateMove {
  previous: string;
  to: string;
  at: string;
  /** An operator's email, or ``agent:<name>``. */
  by: string;
  kind: "set" | "cycle" | "accepted" | "pushed";
  run_id: string;
  note: string;
  question_id: string;
}

export interface NextAction {
  action_id: string;
  type: "contact" | "decision" | "task";
  category: string;
  category_note: string;
  title: string;
  summary: string;
  context: string;
  call_to_action: string;
  /** The passages the ask rests on: one citation per line, with where it sits. */
  sources?: string;
  internal_owner: string;
  /** Why this person owns it; empty on actions written before 12 Oct 2026. */
  owner_reason: string;
  owner_rule: string;
  external_owner: Party | null;
  due: string;
  due_basis: string;
  due_reason: string;
  /** The document that set a record due, as a citation. */
  due_source: string;
  /** Every change to the due date since 13 Oct 2026, oldest first. */
  due_log: DateMove[];
  serves: string;
  /** The id of its own specialist's Ready to build check it works towards, or "". */
  resolves: string;
  /** The other checks the same ask asks for. */
  also_resolves?: string[];
  start_by: string;
  /** A date they gave for their reply, the message that says so, and why a long one is
   * accepted. */
  reply_by: string;
  reply_by_source: string;
  long_wait: string;
  blocking: { since: string; reason: string } | null;
  waits_for: {
    event: string;
    on: "us" | "them";
    /** One item, ended however it closed: waits written before 13 Oct 2026. */
    item: string;
    items: { item: string; needs: WaitNeeds }[];
    /** Who has to act; absent on waits written before 12 Oct 2026. */
    who: Party | null;
    why: string;
  } | null;
  send_with: string[];
  decision_alert_id: string;
  area: string;
  /** The Amazon path step it serves, "" for none. */
  path_step: string;
  /** Its place in its specialist's own ranking, 1 first; null until ranked. */
  priority: number | null;
  /** Why it ranks there; set for every ranked action ("" on one ranked before 13 Oct 2026). */
  priority_reason: string;
  contact_log: ContactEntry[];
  /** What the ask is for, item by item, each with the message that raised it. */
  points: AskPoint[];
  /** Set on an action the platform raised itself, naming what it raised it for. */
  raised_for: string;
  revision: number;
  created_at: string;
  closed: ActionClosed | null;
  status: ActionStatus | null;
}

export interface AskPoint {
  point_id: string;
  text: string;
  /** Who raised it: the organisation, and the person where named. */
  raised_by: string;
  raised_on: string;
  /** `[upload_id "words"]`: the message that raised it. */
  raised_in: string;
  state: "open" | "addressed" | "dropped";
  closed_on: string;
  /** The message that answered it, on an addressed point. */
  closed_in: string;
  closed_note: string;
}

export interface Party {
  name: string;
  org: string;
  role: string;
  email: string;
}

/** How an action closed. Closes recorded before 12 Oct 2026 carry only the first three. */
export interface ActionClosed {
  outcome: string;
  note: string;
  /** When it was recorded; ``happened_on`` is when the event that closed it happened. */
  at: string;
  happened_on?: string;
  reason?: string;
  evidence?: string;
  run_id?: string;
  /** An operator's email, or ``agent:<name>`` (``agent:<name> (review by <email>)``). */
  by?: string;
  chosen_option?: number | null;
}

export interface TrackedDate {
  date_id: string;
  kind: string;
  what: string;
  date: string;
  state: string;
  hardness: "hard" | "soft";
  consequence: string;
  prepare_from: string;
  derived_from: string;
  /** The document that sets the date, when one does: a citation in ``source``, or the
   * upload id alone on older dates. */
  upload_id: string;
  source: string;
  date_log: DateMove[];
  no_action_reason: string;
  area: string;
  closed: { outcome: string; note: string; at: string } | null;
}

/** A register line a check rests on (ready_checks.ReadyCheck.register_lines). */
export interface RegisterLineRef {
  line_id: string;
  document_id: string;
  document_name: string;
  reference: string;
  title: string;
}

/** A graded item's grade (ready_checks.Grade): judged only on whether the document
 * exists, is complete and is executed. */
export interface WarrantyGrade {
  status: "provided" | "partial" | "missing";
  /** Why a partial grade is partial; absent or empty on one from before 20 Oct 2026. */
  gaps?: GradeGap[];
  evidence: { cite: string; page: number | null }[];
  tier: "confirmed" | "verifying" | "third_party";
  to_provide: string;
  resolver: string;
  notes: string;
  filing_issues: {
    cite: string;
    problem: "misfiled" | "truncated" | "wrong_address" | "other";
    note: string;
  }[];
  graded_on: string;
}

/** One roof warranty item on an Amazon building (amazon_items.graded_rows). */
export interface WarrantyDocument {
  key: string;
  text: string;
  clause: string;
  check_id: string;
  met_on: string;
  supplied_by: string;
  grade: WarrantyGrade | null;
  /** Its check closed as withdrawn: the building does not need it. */
  not_needed: boolean;
}

/** One condition on a specialist's Ready to build checklist (ready_checks.py). Met
 * only with ``evidence``, a citation; a met check stays open, ticked. */
export interface ReadyCheck {
  check_id: string;
  text: string;
  met: boolean;
  evidence: string;
  met_on: string;
  /** While unmet: why no open action resolves it. */
  no_action_reason: string;
  /** With no_action_reason: the ids of the items it waits on, any specialist's actions,
   * tracked dates or checks. */
  waits_for: string[];
  area: string;
  /** Where the condition comes from; empty on a check written before 14 Oct 2026. */
  basis:
    | ""
    | "client_spec"
    | "global_data"
    | "project_record"
    | "playbook"
    | "action";
  /** The citation that sets it, or the rule's title for a playbook condition. */
  basis_source: string;
  /** The document register lines that state it, as resolved when it was written. */
  register_lines: RegisterLineRef[];
  created_at: string;
  /** On an Amazon building, the item it records of what we need from Amazon. */
  need?: string;
  closed: { outcome: string; note: string; at: string } | null;
}

/** Who owes the next move in one agent's domain: whoever holds the top open action in
 * its own ranking. Every owner chip reads this. */
export interface NextMove {
  who: "US" | "THEM" | "NOBODY";
  action_id: string;
  title: string;
  waiting_on: string;
  /** Who owes a reply that is overdue: the move is our chase of them. */
  overdue_from: string;
  since: string;
  /** The date the action's row shows (`shownDue`): its due date, or a reply's. */
  due: string;
}

export interface AgentLists {
  version: number;
  next_move: NextMove;
  actions: NextAction[];
  tracked_dates: TrackedDate[];
  ready_checks: ReadyCheck[];
}

export interface ActionDraft {
  draft_id: string;
  to: string;
  subject: string;
  body: string;
  why: string;
  author: string;
  created_at: string;
  /** The upload id of the email thread it replies on; "" for a new conversation. */
  thread: string;
}

export interface ShownSection {
  section_id: string;
  title: string;
  /** "" while the agent has not written it. */
  body: string;
}

export type LandlordPackStage =
  | "to_amazon"
  | "to_landlord"
  | "feedback"
  | "signed";

export interface LandlordPackEntry {
  entry_id: string;
  /** The day the email was sent. */
  date: string;
  kind: string;
  kind_label: string;
  /** How the page marks it: a send, feedback, a reply that is not feedback, or other. */
  tone: "send" | "feedback" | "reply" | "other";
  /** The stage this entry dates, as the first of its kind; "" for none. */
  dates_stage: LandlordPackStage | "";
  sender: string;
  recipients: string[];
  /** `[upload_id "words"]`: the email it rests on. */
  email: string;
  /** The email refers to the send rather than being it. */
  referenced: boolean;
  summary: string;
  /** On a reply from the landlord's side: each point it raised. */
  points: string[];
  /** Set on an entry Legal withdrew, with why. */
  withdrawn_reason: string | null;
}

export interface LandlordPackHistory {
  /** Each stage's first email, null where none is logged. */
  stages: Record<
    LandlordPackStage,
    { date: string; sender: string; email: string } | null
  >;
  /** Latest email first. */
  entries: LandlordPackEntry[];
}

/** One entry of a building's structural history, by its id, as a tile or a version shows it. */
export interface StructuralBrief {
  entry_id: string;
  date: string;
  sender: string;
  email: string;
  /** "MJM P02", "(draft)" on a draft; "" for an entry that is not a version. */
  version: string;
}

export interface StructuralReport {
  firm: string;
  role: "ours" | "client" | "third_party" | "mounting";
  reference: string;
  revision: string;
  status: "complete" | "draft";
  verdict:
    | "adequate"
    | "adequate_with_works"
    | "not_adequate"
    | "inconclusive"
    | "not_stated";
  /** The existing structure fails without PV; null where the report does not say. */
  existing_fails: boolean | null;
  works: StructuralWork[];
  /** The share of the roof the report says can take PV; null where it does not say. */
  roof_viable: {
    min_pct: number;
    max_pct: number;
    after_works: boolean;
    reason: string;
  } | null;
  /** The share as the roof stands, beside a figure that holds only after works; null
   * where the report does not state it. */
  roof_viable_now: { min_pct: number; max_pct: number; reason: string } | null;
  client_spec: string;
  mounting_design: string;
  /** `[upload_id "words"]`: the report itself, where it is held. */
  document: string;
}

/** A work a version requires: when the report says it is needed, and where it stands. */
export interface StructuralWork {
  text: string;
  timing: "before_pv" | "after_pv" | "not_stated";
  status: "required" | "agreed" | "done";
  /** The entry that agreed it or reported it done; null while only required. */
  status_entry: StructuralBrief | null;
}

export interface StructuralVersionReport extends StructuralBrief {
  report: StructuralReport;
}

export interface StructuralVersion extends StructuralVersionReport {
  /** The version the assessment rests on. */
  relied_on: boolean;
  /** A later complete revision from the same firm exists, which a choice of this one
   * would rely on instead. */
  superseded: boolean;
  /** The entry accepting it, if any. */
  accepted: StructuralBrief | null;
}

/** One open feedback point, under the ask it sits on. */
export interface StructuralFeedbackPoint {
  point_id: string;
  text: string;
  raised_by: string;
  raised_on: string;
  raised_in: string;
  action_id: string;
}

export interface StructuralEntry {
  entry_id: string;
  date: string;
  kind: string;
  kind_label: string;
  tone:
    | "send"
    | "reply"
    | "report"
    | "feedback"
    | "accept"
    | "works"
    | "access"
    | "remediation"
    | "defect"
    | "other";
  sender: string;
  recipients: string[];
  /** `[upload_id "words"]`, `[run <id> "words"]` for an operator's confirmation, or the
   * operator's own words for a choice made on the card. */
  email: string;
  summary: string;
  points: string[];
  /** The version it is about, "" for none. */
  about: string;
  /** On an issued version: its label. */
  version: string;
  /** Keys of the Amazon items the message delivered. */
  delivers: string[];
  /** The project the cited document is filed under, where it is another building's. */
  filed_under: string;
  withdrawn_reason: string | null;
}

/** The feedback points on an area's asks, as every readiness history card shows them
 * (readiness_points.feedback_payload). */
export interface ReadinessFeedback {
  points_open: number;
  points_total: number;
  /** Each party's open feedback points, the party owing most first. */
  feedback: {
    party: string;
    asks: { action_id: string; title: string }[];
    points: StructuralFeedbackPoint[];
  }[];
}

/** One party's acceptance of a version, and whether it completes the step. */
export interface ReadinessAcceptance extends StructuralBrief {
  party: string;
  completes: boolean;
}

export interface RoofDefect {
  what: string;
  where: string;
  seen_by: "observed" | "site" | "landlord" | "client" | "not_stated";
  timing: "before_pv" | "after_pv" | "not_stated";
}

/** What a roof report version states (roof_history.parse_report). */
export interface RoofReport {
  firm: string;
  source: "ours" | "client" | "landlord" | "independent";
  reference: string;
  revision: string;
  status: "complete" | "draft";
  phase: "before_install" | "after_works" | "after_install";
  coverage: "whole_roof" | "pv_zones" | "part" | "not_stated";
  methods: ("walked" | "drone" | "leak_test" | "desk")[];
  rating: string;
  defects: RoofDefect[];
  repairs_before_pv: "none" | "required" | "not_stated";
  remaining_life: string;
  warranty: { exists: "yes" | "no" | "not_stated"; holder: string; effect: string };
  recommendation: string;
  document: string;
}

export interface ReadinessVersion<R> extends StructuralBrief {
  report: R;
  relied_on: boolean;
  /** Every party's acceptance of it, oldest first. */
  accepted: ReadinessAcceptance[];
}

export interface RoofHistory extends ReadinessFeedback {
  state: { label: string; tone: StructuralHistory["state"]["tone"] };
  relied_on: StructuralBrief | null;
  acceptance: StructuralBrief | null;
  passed_on: StructuralBrief | null;
  /** The acceptance that makes the roof ready for PV; null while it is not. */
  ready: StructuralBrief | null;
  after_install: { report: StructuralBrief | null; accepted: StructuralBrief | null };
  /** Complete reports that differ on repairs before PV. */
  disagree: StructuralBrief[];
  remediation: {
    stage: string;
    label: string;
    entry: StructuralBrief | null;
    scope: { what: string; zone: string; by: string; pays: string } | null;
    scope_entry: StructuralBrief | null;
    approval: StructuralBrief | null;
    route: string;
    declined: StructuralBrief | null;
    done: StructuralBrief | null;
    thread: (StructuralBrief & { kind_label: string; summary: string })[];
  } | null;
  /** Latest first. */
  versions: ReadinessVersion<RoofReport>[];
  /** Latest first. */
  entries: StructuralEntry[];
}

/** What a survey report version states (survey_history.parse_report). */
export interface SurveyReport {
  firm: string;
  source: "ours" | "client" | "independent";
  reference: string;
  revision: string;
  status: "complete" | "draft";
  visits: string[];
  access: {
    reached_by:
      | "internal_stairs"
      | "hatch"
      | "ladder"
      | "mewp"
      | "drone_only"
      | "not_stated";
    unseen: string;
  };
  area_surveyed: {
    extent: "whole_roof" | "pv_zones" | "part" | "not_stated";
    excluded: string;
  };
  exclusion_zones: { zone: string; reason: string; set_by: string }[];
  cable_routes: string;
  equipment: string;
  dno_position: string;
  open_questions: string[];
  document: string;
}

export interface SurveyHistory extends ReadinessFeedback {
  state: { label: string; tone: StructuralHistory["state"]["tone"] };
  relied_on: StructuralBrief | null;
  acceptance: StructuralBrief | null;
  /** A complete version issued since the last acceptance, not accepted yet. */
  newer: StructuralBrief | null;
  passed_on: StructuralBrief | null;
  /** Getting onto the roof, in every area, oldest first. */
  access: (StructuralBrief & {
    kind: string;
    kind_label: string;
    summary: string;
    /** The history it is logged in. */
    area: "structural" | "roof_condition" | "on_site_survey";
  })[];
  /** Latest first. */
  versions: ReadinessVersion<SurveyReport>[];
  /** Latest first. */
  entries: StructuralEntry[];
}

/** Building readiness's histories, by area (readiness_areas.cards). */
export interface ReadinessHistories {
  structural: StructuralHistory;
  roof_condition: RoofHistory;
  on_site_survey: SurveyHistory;
}

export interface StructuralHistory extends ReadinessFeedback {
  /** The version the latest decision names, else the latest complete one; null while
   * firms disagree undecided. */
  relied_on: StructuralBrief | null;
  /** Who decided which version we rely on, and when; null where nobody did. */
  decision: (StructuralBrief & { kind_label: string }) | null;
  /** Complete versions from different firms that disagree, nobody having decided. */
  disputed: StructuralVersionReport[];
  /** The card's state, first that applies (structural_status._state). */
  state: {
    label: string;
    tone: "dispute" | "wait" | "none" | "works" | "limits" | "accepted";
  };
  /** The version relied on's Roof viable, with the kWp it is of the max-out design
   * Design readiness recorded (null with none recorded, or on an earlier version). */
  roof_viable: {
    text: string;
    kwp: { of: number; min: number; max: number } | null;
  } | null;
  /** The version relied on's works needed before PV and not done; null with none relied on. */
  before_pv: {
    open: { text: string; status: StructuralWork["status"] }[];
    done: number;
    untimed: number;
    total: number;
  } | null;
  signoff: {
    /** The acceptance (or an operator's confirmation) the sign-off rests on. */
    accepted: StructuralBrief | null;
    /** The version it accepted. */
    of: StructuralBrief | null;
    /** A complete version issued since the last acceptance, not accepted yet. */
    newer: StructuralBrief | null;
    /** The latest send of the relied-on version for review. */
    passed_on: StructuralBrief | null;
  };
  /** Latest first. */
  versions: StructuralVersion[];
  /** Latest first. */
  entries: StructuralEntry[];
}

export interface ProjectAgentStatus {
  agent: string;
  label: string;
  // Has a status row and a project tab of its own.
  keeps_status_row: boolean;
  // Its row keeps an Applications box.
  keeps_applications: boolean;
  // Its row keeps Building readiness's three areas.
  keeps_readiness: boolean;
  // Its file carries the project's contract stages.
  keeps_contracts: boolean;
  // It logs the landlord consent pack's emails.
  keeps_landlord_pack: boolean;
  /** Its landlord consent pack history as of the version shown; null where it keeps none,
   * or the building has no pack to track (not Amazon's, nothing logged). */
  landlord_pack: LandlordPackHistory | null;
  // It keeps the structural, roof condition and on-site survey histories.
  keeps_readiness_histories: boolean;
  /** Its readiness histories as of the version shown; null where it keeps none. */
  readiness_histories: ReadinessHistories | null;
  /** On an Amazon building, the roof warranty items it grades, for its Roof condition
   * tab's "Documents and warranties status"; null elsewhere and for any other agent. */
  warranty_documents?: WarrantyDocument[] | null;
  /** Sections of its file its tab shows as cards of their own, in order. */
  sections: ShownSection[];
  enrolled: boolean;
  // Set when the LAST scheduled run failed on this project or never reached it.
  attention: string | null;
  version: number;
  updated_at: string | null;
  set_by_run_id: string | null;
  // Null on an agent enrolled but not yet worked, and on one that keeps no row.
  row: PortfolioRow | null;
  /** Its next actions and tracked dates; null before it has written any. */
  lists: AgentLists | null;
  /** The Timeline section of its file, one entry per event, oldest first as written. */
  timeline: string[];
  /** Its Agent feedback questions on this project waiting on an operator: unanswered,
   * or asked back by a review. 0 on an earlier version. */
  feedback_waiting: number;
}

export interface ProjectAgentStatusList {
  project_id: string;
  /** The briefing version shown, and the newest there is. */
  briefing_version: number;
  latest_version: number;
  /** Set when an earlier version was asked for. */
  as_of: string | null;
  /** On an earlier version only: the dashboard row as it was rendered then. */
  dashboard_row: DashboardRow | null;
  attribute_definitions: AttributeDef[] | null;
  /** The latest scheduled run that worked the project: its open emails and questions. */
  latest_run_id: string | null;
  /** When that pass started. */
  latest_run_at: string | null;
  /** "scheduled" for a pass; "chat" for a review conversation that updated the project. */
  latest_run_kind: string | null;
  /** A pass rebuilding the briefing from review comments right now. */
  applying_review: ApplyingReview | null;
  /** ``{email: name}`` for owners. */
  team: Record<string, string>;
  /** ``{category key: name}`` from the action categories document. */
  category_names: Record<string, string>;
  /** The unsent email drafted for each action (today's only). */
  action_drafts: Record<string, ActionDraft>;
  /** The actions an operator dismissed until the project's next scheduled pass, with who
   * and when (today's only; absent from a server older than it). */
  action_dismissals?: Record<string, ActionDismissal>;
  /** ``{action id: {intent: run id}}``: the conversation each button already started. */
  action_conversations: Record<string, Partial<Record<ActionIntent, string>>>;
  /** The day statuses were derived for. */
  lists_today: string;
  /** Each open date move, by the action or tracked date it would move (today's only). */
  date_moves: Record<string, DateMoveSubject>;
  agents: ProjectAgentStatus[];
}

/** What an action table reads of a project: every specialist's lists and the
 * project-level parts beside them. ``/projects/agent-status`` and
 * ``/dashboard/actions`` both serve it. */
export type ActionListsData = Pick<
  ProjectAgentStatusList,
  | "team"
  | "category_names"
  | "action_drafts"
  | "action_conversations"
  | "lists_today"
  | "date_moves"
> &
  // A review in flight on the project, whose specialists' actions are about to change.
  Partial<Pick<ProjectAgentStatusList, "applying_review" | "action_dismissals">> & {
    agents: Pick<ProjectAgentStatus, "agent" | "label" | "lists">[];
  };

/** An operator's "Dismiss until next run" on an action (action_dismissals.py). */
export interface ActionDismissal {
  by: string;
  at: string;
}

/** An agent's proposal to move a date in force: a ``date_move`` Agent feedback question's
 * subject (date_moves.subject_for), with the question it is answered on. */
export interface DateMoveSubject {
  type: "date_move";
  agent: string;
  item_kind: "action" | "tracked_date";
  item_id: string;
  field: "due" | "date";
  title: string;
  current: string;
  proposed: string;
  proposed_basis: string;
  proposed_source: string;
  /** An action's date may be answered with another date; a tracked date's may not. */
  push_allowed: boolean;
  question_id: string;
  reason: string;
  proposed_at: string;
  run_id: string;
}

/** What a next action's button asks for (action_requests.py INTENTS). */
export type ActionIntent = "draft_email" | "draft_reminder" | "discuss";


/** ``version`` null reads the latest; a number reads that earlier briefing version. */
export const useProjectAgentStatus = (
  projectId: string | null,
  version: number | null = null,
) => {
  const http = useAgentsHttp();
  return useQuery<ProjectAgentStatusList>(
    [QK.projectAgentStatus, projectId, version],
    () =>
      http<ProjectAgentStatusList>("/projects/agent-status", {
        project_id: projectId,
        ...(version !== null ? { version } : {}),
      }),
    {
      enabled: !!projectId,
      refetchInterval: (data) =>
        data?.applying_review ? APPLYING_POLL_MS : false,
    },
  );
};

// Days between an ISO date and today, computed at render: elapsed time is never
// stored, so a row cannot go stale by sitting still (portfolio_row.py).
export const daysSince = (isoDate: string): number | null => {
  const then = Date.parse(`${isoDate}T00:00:00Z`);
  if (Number.isNaN(then)) return null;
  const today = Date.parse(new Date().toISOString().slice(0, 10));
  return Math.round((today - then) / 86_400_000);
};
